import { AddIcon, ArrowBackIcon, CheckIcon, DeleteIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import {
  AspectRatio,
    Badge,
    Box, Button, Flex, FormLabel, Heading, HStack, IconButton, Image, Input, Link,
    Menu, MenuButton, MenuItem, MenuList,
    Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Radio,
    RadioGroup,
    SimpleGrid,
    SkeletonText,
    Spinner,
    Text, Tooltip, useColorModeValue, useDisclosure, useToast, VStack
} from "@chakra-ui/react";
import DeleteConfirmationModal from "components/genericForms/DeleteConfirmationModal";
import { useFormik } from "formik";
import { useEffect, useRef } from "react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { BsThreeDotsVertical } from "react-icons/bs";
import { GoVideo } from "react-icons/go";
import { useCreateAvatarMutation, useGetAvatarsCountQuery, useGetAvatarsQuery, useRemoveAvatarMutation } from "store/aiHub/aiGenerationApi";
import * as Yup from "yup";

const TILE = {
  borderRadius: 10,
  padding: 4, 
  borderWidth: 1,
};

/* ---------------- Helpers ---------------- */
const getVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const vid = document.createElement("video");
    vid.preload = "metadata";
    vid.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(vid.duration); };
    vid.onerror = () => { URL.revokeObjectURL(url); reject(new Error("metadata error")); };
    vid.src = url;
  });

const MIN_SECONDS = 120;
const MIN_SECONDS_FOR_SOURCE = 440;
const REQ_MSG = "To ensure your avatar works perfectly in our product, we just need your video to meet a few requirements:\n Video length should be more than 2 minutes.";
const deleteMessage = `1. Rendered videos using this avatar will remain unaffected.\n2. Draft videos with this avatar won’t be able to render.\n3. New videos cannot be created with this avatar.`;

function HoverPreviewCard({ avatar, isSelected, onSelect }) {
  const checkIconColor = useColorModeValue("brand.800", "purple.800");
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);

  const isReady = avatar?.process_status === "ready to use";
  const canHoverVideo = isReady && !!avatar.preview_video_1_1;

  useEffect(() => {
    if (!canHoverVideo || !videoRef.current) return;
    if (isHovered) {
      videoRef.current.play().catch((err) => {
        console.warn("Video playback failed:", err);
      });
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isHovered, canHoverVideo]);

  return (
    <Box
      onClick={onSelect}
      role="button"
      aria-label={`Select ${avatar?.creator_name}`}
      w="100%"
      h="100%"
      overflow="hidden"
      borderRadius={TILE.borderRadius}
      borderWidth={TILE.borderWidth}
      borderColor={isSelected ? "brand.800" : "gray.200"}
      _hover={{ shadow: "md", cursor: "pointer", borderColor: "brand.800" }}
      position="relative"
      p={0} // the media itself spans edge-to-edge inside the tile shell
    >
      {isSelected && (
        <Box position="absolute" top="8px" left="8px" zIndex={3}>
          <CheckIcon
            color="white"
            bg={checkIconColor}
            borderRadius="full"
            p="1"
            boxSize={5}
          />
        </Box>
      )}

      {/* Base image */}
      <Image
        src={avatar.preview_image_1_1}
        alt={avatar.creator_name}
        objectFit="cover"
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        style={{
          opacity: canHoverVideo ? (isHovered ? 0 : 1) : 1,
          transition: canHoverVideo ? "opacity 0.3s ease-in-out" : undefined,
        }}
      />

      {/* Spinner only when we intend to show video */}
      {canHoverVideo && isHovered && isLoading && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={2}
        >
          <Spinner size="md" color="white" />
        </Box>
      )}

      {/* Hover video */}
      {canHoverVideo && (
        <video
          ref={videoRef}
          src={avatar.preview_video_1_1}
          muted
          loop
          playsInline
          onCanPlay={() => setIsLoading(false)}
          onWaiting={() => setIsLoading(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
      )}
    </Box>
  );
}

/* ---------------- Add / Create card ---------------- */
function CreateAvatarCard({ onOpen, selectedAvatar, onSelectAvatar }) {
  const addBoxBorder = useColorModeValue("gray.200", "purple.800");
  const addBoxHoverBackground = useColorModeValue("gray.50", "gray.700");
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  const {
    isOpen: deleteModalIsOpen,
    onOpen: openDeleteModal,
    onClose: closeDeleteModal,
  } = useDisclosure();

  const {
    data: avatarsData,
    isLoading: isLoadingavatarsData,
    refetch: refetchAvatars,
  } = useGetAvatarsQuery();

  const {
    data: avatarsCount,
    isLoading: isLoadingAvatarsCount,
    refetch: refetchAvatarsCount,
    isFetching: isFetchingAvatarsCount,
  } = useGetAvatarsCountQuery();

  const [removeAvatar, { isLoading }] = useRemoveAvatarMutation();

  const slotsAvailable = avatarsCount
    ? avatarsCount?.max_count - avatarsCount?.current_count
    : 0;
  const disabled = slotsAvailable <= 0;

  const handleOpenDeleteModal = (avatarId) => {
    setSelectedAvatarId(avatarId);
    openDeleteModal();
  };

  const handleConfirmDelete = async () => {
    try {
      await removeAvatar(selectedAvatarId).unwrap();
      refetchAvatars();
      refetchAvatarsCount();
      closeDeleteModal();
      setSelectedAvatarId(null);
    } catch (error) {
      console.error("Failed to delete avatar:", error);
    }
  };

  const deleteMessage = "Are you sure you want to delete this avatar?";

  return (
    <>
      <DeleteConfirmationModal
        isOpen={deleteModalIsOpen}
        onClose={() => {
          closeDeleteModal();
          setSelectedAvatarId(null);
        }}
        onConfirm={handleConfirmDelete}
        message={deleteMessage}
        isLoading={isLoading}
      />

      <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
        {/* Create tile — SAME shell */}
        <Tooltip
          label={disabled ? "No slots available" : undefined}
          isDisabled={!disabled}
          placement="top"
        >
          <Box>
            <AspectRatio ratio={1} w="100%">
              <Flex
                borderWidth={TILE.borderWidth}
                borderColor={addBoxBorder}
                borderRadius={TILE.borderRadius}
                p={TILE.padding}
                w="100%"
                h="100%"
                _hover={{
                  bg: disabled ? undefined : addBoxHoverBackground,
                  cursor: disabled ? "not-allowed" : "pointer",
                  borderColor: disabled ? addBoxBorder : "brand.800",
                  shadow: disabled ? undefined : "md",
                }}
                alignItems="center"
                justifyContent="center"
                direction="column"
                onClick={disabled ? undefined : onOpen}
                role={disabled ? "img" : "button"}
                aria-disabled={disabled}
                opacity={disabled ? 0.6 : 1}
              >
                <AddIcon boxSize={5} mb={3} />
                {isLoadingAvatarsCount || isFetchingAvatarsCount ? (
                  <SkeletonText height="10px" width="100px" noOfLines={2} />
                ) : (
                  <>
                    <Text fontSize="md" fontWeight="semibold">
                      Create avatar
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {slotsAvailable} slot{slotsAvailable === 1 ? "" : "s"} available
                    </Text>
                  </>
                )}
              </Flex>
            </AspectRatio>
          </Box>
        </Tooltip>

        {/* Avatar tiles — SAME shell */}
        {isLoadingavatarsData ? (
          <Flex align="center" justify="center" minH="200px">
            <Spinner />
          </Flex>
        ) : (
          avatarsData?.map((avatar) => {
            const isRejected = avatar.process_status === "rejected";
            const isReady = avatar.process_status === "ready to use";
            const isSelected = selectedAvatar?.id === avatar.id;

            return (
              <Box key={avatar.id}>
                {isReady ? (
                  <AspectRatio ratio={1} w="100%">
                    <HoverPreviewCard
                      avatar={avatar}
                      isSelected={isSelected}
                      onSelect={() => isReady && onSelectAvatar?.(avatar)}
                    />
                  </AspectRatio>
                ) : (
                  <AspectRatio ratio={1} w="100%">
                    <Flex
                      borderWidth={TILE.borderWidth}
                      borderColor="gray.200"
                      borderRadius={TILE.borderRadius}
                      p={TILE.padding}
                      w="100%"
                      h="100%"
                      position="relative"
                      direction="column"
                      _hover={{ borderColor: "purple.200" }}
                      cursor="pointer"
                      role="group"
                    >
                      {/* 3-dot menu icon */}
                      <Box
                        position="absolute"
                        top="10px"
                        right="10px"
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                      >
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            size="sm"
                            variant="ghost"
                            icon={<BsThreeDotsVertical />}
                            aria-label="Options"
                          />
                          <MenuList minWidth="100px" width="auto">
                            <MenuItem
                              icon={<DeleteIcon />}
                              fontSize="12px"
                              onClick={() => handleOpenDeleteModal(avatar.id)}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Box>

                      {/* Content area (kept within same padding/spacing) */}
                      <Text fontWeight="semibold" mb={2} noOfLines={1}>
                        {avatar.creator_name}
                      </Text>

                      <Flex direction="column" mb={2} gap={1}>
                        <Text fontSize="sm" color="gray.500" noOfLines={1}>
                          Style: {avatar.style}
                        </Text>
                        <Text fontSize="sm" color="gray.500" noOfLines={1}>
                          Gender:{" "}
                          {avatar.gender.charAt(0).toUpperCase() +
                            avatar.gender.slice(1)}
                        </Text>
                      </Flex>

                      {/* Badges at bottom-left */}
                      <Flex
                        position="absolute"
                        bottom="10px"
                        left="10px"
                        gap={2}
                        flexWrap="wrap"
                      >
                        {isRejected ? (
                          <Tooltip
                            label={avatar.failed_reason}
                            placement="top"
                            fontSize="12px"
                          >
                            <Badge
                              colorScheme="red"
                              size="xs"
                              p={1}
                              py={0.5}
                              borderRadius="sm"
                            >
                              {avatar.process_status} <InfoOutlineIcon ml={1} />
                            </Badge>
                          </Tooltip>
                        ) : (
                          <Badge
                            colorScheme="gray"
                            size="xs"
                            p={1}
                            py={0.5}
                            borderRadius="sm"
                          >
                            {avatar.process_status}
                          </Badge>
                        )}
                      </Flex>
                    </Flex>
                  </AspectRatio>
                )}
              </Box>
            );
          })
        )}
      </SimpleGrid>
    </>
  );
}
  
/* ---------------- Modal ---------------- */
function CreateAvatarModal({ isOpen, onClose, refetchAvatars }) {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [createAvatar, { isLoading: isCreatingAvatar }] = useCreateAvatarMutation();
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const panelBg = useColorModeValue("gray.900", "gray.800");

  const formik = useFormik({
    initialValues: {
      sourceVideo: null,      
      sourceDuration: 0,
      consentVideo: null,      
      consentDuration: 0,   
      name: "",    
      gender: "nb",            
    },
    validationSchema: Yup.object({
      sourceVideo: Yup.mixed().required("Please upload video source footage."),
      consentVideo: Yup.mixed().required("Please upload consent video."),
      name: Yup.string().trim().required("Please input name."),
      gender: Yup.string().oneOf(["m", "f", "nb"]).required("Please select a gender."),
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
        
        const formData = new FormData();
        formData.append("lipsync_input", values.sourceVideo);
        formData.append("consent_video", values.consentVideo);
        formData.append("creator_name", values.name.trim());
        formData.append("gender", values.gender);
      
        setSubmitting(true);
        
        createAvatar(formData)
          .unwrap()
          .then(() => {
            toast({
              title: "Avatar submitted",
              status: "success",
              duration: 3000,
              description: "Your avatar is under review. It will be visible once approved.",
              isClosable: true,
              position: "top-right", 
            });

            refetchAvatars();
            resetForm();
            setStep(1);
            onClose();
          })
          .catch((err) => {
            toast({
              title: "Submission failed",
              description: err?.data?.message || "Please try again.",
              status: "error",
              duration: 4000,
              isClosable: true,
              position: "top-right", 
            });
          })
          .finally(() => {
            setSubmitting(false);
          });
    }      
  });

  const { values, errors, touched, setFieldValue, handleSubmit, isSubmitting } = formik;

  /* ---------- Dropzones ---------- */
  const onDropSource = async (accepted) => {
    const f = accepted?.[0];
    if (!f) return;
    try {
      const dur = await getVideoDuration(f);
      await setFieldValue("sourceVideo", f);
      await setFieldValue("sourceDuration", dur);
  
      // NEW: show Formik error if duration > 2 min
      if (dur > MIN_SECONDS_FOR_SOURCE) {
        formik.setFieldError("sourceDuration", REQ_MSG);
      } else {
        formik.setFieldError("sourceDuration", undefined);
      }
    } catch {
      toast({ title: "Couldn’t read video", status: "error", duration: 3000 });
    }
  };

  const {
    getRootProps: getSrcRootProps,
    getInputProps: getSrcInputProps,
    open: openSourcePicker
  } = useDropzone({
    onDrop: onDropSource,
    accept: { "video/*": [".mp4", ".mov", ".webm"] },
    maxSize: 1000 * 1024 * 1024,
    multiple: false,
    noClick: true, 
  });
  

    const onDropConsent = async (accepted) => {
    const f = accepted?.[0];
    if (!f) return;
    try {
        const dur = await getVideoDuration(f);
        await setFieldValue("consentVideo", f);
        await setFieldValue("consentDuration", dur);

        // NEW: show Formik error if duration > 2 min
        if (dur > MIN_SECONDS) {
        formik.setFieldError("consentDuration", REQ_MSG);
        } else {
        formik.setFieldError("consentDuration", undefined);
        }
    } catch {
        toast({ title: "Couldn’t read video", status: "error", duration: 3000 });
    }
    };

    const {
        getRootProps: getConsentRootProps,
        getInputProps: getConsentInputProps,
        open: openConsentPicker
      } = useDropzone({
        onDrop: onDropConsent,
        accept: { "video/*": [".mp4", ".mov", ".webm"] },
        maxSize: 200 * 1024 * 1024,
        multiple: false,
        noClick: true,
    });
      

  /* ---------- Gating & tooltips ---------- */
  const sourceTooShort = values.sourceVideo && values.sourceDuration < MIN_SECONDS;
  const blockStep2 = !values.sourceVideo || sourceTooShort;
  const continueTooltip = !values.sourceVideo
    ? "Please upload video source footage."
    : "Video length should be more than 2 minutes.";

  const missingName = !values.name?.trim();
  const missingConsent = !values.consentVideo;

  const blockSubmit = missingName || missingConsent;
  const submitTooltip =
    missingName && missingConsent
      ? "Please input name and upload consent video."
      : missingName
      ? "Please input name."
      : "Please upload consent video.";

  const handleClose = () => { formik.resetForm(); setStep(1); onClose(); };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="5xl" isCentered>
      <ModalOverlay />
      <ModalContent h="90vh">
        <ModalHeader><ModalCloseButton /></ModalHeader>

        <ModalBody>
          {/* -------- Step 1 -------- */}
          {step === 1 && (
            <Box maxH={{ base: '600px', md: '550px'}} h={'auto'} overflowY="auto" maxW="600px" mx="auto" w="100%" p={2}>
              <VStack align="stretch" spacing={6}>
                <VStack spacing={1} textAlign="center">
                  <Text>Step 1/3</Text>
                  <Text>Instructions</Text>
                  <Heading size="sm">Bring your own avatar</Heading>
                  <Text color={mutedText}>
                    Film yourself speaking freely on any topic. Use an expressive tone that reflects your avatar’s desired voice.
                  </Text>
                </VStack>

                <Box bg={panelBg} borderRadius="lg" overflow="hidden" mx="auto">
                  <video
                    style={{ width: "500px", height: "auto", display: "block" }}
                    src="https://d35ghwdno3nak3.cloudfront.net/creatify-made-videos/BYOA%20Video%20Guide.mp4"
                    controls
                    controlsList="nodownload"
                  />
                </Box>

                <Box textAlign="center">
                  <Link href="#" onClick={(e) => e.preventDefault()} fontWeight="semibold">
                    Beta program detailed guide
                  </Link>
                </Box>

                <Flex gap={10} flexDir={{ base: "column", md: "row" }} px={10} fontSize="12px">
                  <VStack align="start" flex={1} spacing={1}>
                    <Heading size="sm">Dos</Heading>
                    <Box pl={2}>
                      <Text>Upload footage lasting 2 to 5 minutes.</Text>
                      <Text>Position yourself at the center of the frame.</Text>
                      <Text>Ensure you’re visible from lower chest upwards.</Text>
                      <Text>Look directly into the camera.</Text>
                      <Text>Use hand gestures to add realism.</Text>
                      <Text>High resolution (&gt; 720p), 16:9.</Text>
                      <Text>Record in a well-lit environment.</Text>
                    </Box>
                  </VStack>

                  <VStack align="start" flex={1} spacing={1}>
                    <Heading size="sm">Donts</Heading>
                    <Box pl={2}>
                      <Text>Don’t stitch or edit your footage.</Text>
                      <Text>Avoid shifting your position.</Text>
                      <Text>Don’t turn your head to the side.</Text>
                      <Text>Keep face unobstructed.</Text>
                      <Text>Avoid exaggerated expressions.</Text>
                    </Box>
                  </VStack>
                </Flex>
              </VStack>
            </Box>
          )}

          {/* -------- Step 2 -------- */}
          {step === 2 && (
            <Box maxW="600px" mx="auto" w="100%" p={2}>
                <VStack align="stretch" spacing={6}>
                <VStack spacing={1} textAlign="center">
                    <Text>Step 2/3</Text>
                    <Text>Upload</Text>
                    <Heading size="sm">Bring your own avatar</Heading>
                </VStack>

                {!values.sourceVideo ? (
                    <Box
                       width={{ base: '100%', md: '500px' }} mx="auto" role="group"
                        borderWidth="1px" borderStyle="dashed" borderRadius="md" borderColor="gray.300"
                        _hover={{ borderColor: "purple.400" }} transition="border-color .2s ease"
                        px={{ base: 4, md: 10 }} py={{ base: 10, md: 16 }}
                        textAlign="center" cursor="pointer"
                        {...getSrcRootProps()} 
                    >
                        <input {...getSrcInputProps()} />
                        <Flex justify="center" mb={4} opacity={0.8} _groupHover={{ opacity: 1 }}>
                            <GoVideo size={40} />
                        </Flex>
                        <Heading size="sm" mb={1}>Drop video here to upload</Heading>
                        <Text fontSize="12px" color={mutedText}>
                            You can upload videos up to 1000 MB in size. We support MP4, MOV, and WebM formats.
                        </Text>
                        <Button colorScheme={'brand'} variant="link" mt={4} onClick={openSourcePicker}>Browse local files</Button>
                    </Box>
                ) : (
                    <Box
                        width={{ base: "100%", md: "500px" }} 
                        maxW="100%"           
                        aspectRatio={16 / 9}
                        mx="auto"
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor="gray.200"
                        overflow="hidden"
                        position="relative"
                        _hover={{ borderColor: "purple.400", border: "1px dashed" }}
                        >
                        <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            position="absolute"
                            variant="ghost"
                            bg="rgba(0,0,0,0.5)"
                            color="white"
                            top="8px"
                            right="8px"
                            zIndex="2"
                            aria-label="Delete video"
                            onClick={() => {
                            setFieldValue("sourceVideo", null);
                            setFieldValue("sourceDuration", 0);
                            formik.setFieldError("sourceDuration", undefined);
                            }}
                            _hover={{ bg: "purple.400" }}
                        />
                        <video
                            controls
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                            }}
                            src={URL.createObjectURL(values.sourceVideo)}
                        />
                        </Box>

                )}

                {touched.sourceVideo && (errors.sourceVideo || sourceTooShort) && (
                    <Text color="red.400" textAlign="center" fontSize="sm">
                    {errors.sourceVideo || "Video length should be more than 2 minutes."}
                    </Text>
                )}

                    {errors.sourceDuration && values.sourceDuration > MIN_SECONDS && (
                    <Text color="red.400" textAlign="center" fontSize="sm" mt={2} whiteSpace="pre-line">
                        {errors.sourceDuration}
                    </Text>
                    )}
                </VStack>
            </Box>
          )}

          {/* -------- Step 3 -------- */}
          {step === 3 && (
            <>
                {isCreatingAvatar ? (
                    <Flex
                        direction="column"
                        width="full"
                        alignItems="center"
                        justifyContent="center"
                        h="60vh"
                        gap={2}
                    >
                    <Image
                        src="/Ai_ImageLoader.gif"
                        alt="generating..."
                        boxSize="200px"
                        objectFit="contain"
                        width="100%"
                    />
                    <Flex direction="column" alignItems="center" gap={2}>
                        <Text fontWeight="bold" fontSize="14px">
                        Creating your avatar...
                        </Text>
                    </Flex>
                    </Flex>
                ) : (
                    <Box width={{ base: '100%', md: '500px' }} mx="auto">
                        <VStack align="stretch" spacing={6}>
                        <VStack spacing={1} textAlign="center">
                            <Text>Step 3/3</Text>
                            <Text>Consent & Details</Text>
                            <Heading size="sm">Please record yourself reading the consent script and upload your video:</Heading>
                        </VStack>

                        {/* Gender */}
                        <Box>
                            <FormLabel fontSize="sm">Gender</FormLabel>
                            <RadioGroup
                                colorScheme="brand"
                                onChange={(val) => setFieldValue("gender", val)}
                                value={values.gender}
                            >
                                <HStack spacing={4}>
                                    <Radio value="m">Male</Radio>
                                    <Radio value="f">Female</Radio>
                                    <Radio value="nb">Non-Binary</Radio>
                                </HStack>
                            </RadioGroup>
                        </Box>

                        {/* Name */}
                        <Box>
                            <Text fontSize="14px" lineHeight="1.6" alignItems={'center'}>
                                I,&nbsp;
                                <Box
                                as="span"
                                display="inline-block"
                                verticalAlign="middle"
                                maxW="220px"
                                w="full"
                                >
                                <Input
                                    size="sm"
                                    borderRadius="md"
                                    placeholder="Enter your full name"
                                    value={values.name}
                                    onChange={(e) => setFieldValue("name", e.target.value)}
                                />
                                </Box>
                                &nbsp;, hereby grant Creatify permission to use footage of me for the purpose of
                                creating a custom Creatify avatar, solely for my use on my own team on the Creatify platform.
                            </Text>
                        </Box>


                        {/* Consent upload */}
                        {!values.consentVideo ? (
                            <Box
                              mx="auto" role="group"
                              borderWidth="1px" borderStyle="dashed" borderRadius="md" borderColor="gray.300"
                              _hover={{ borderColor: "purple.400" }}
                              transition="border-color .2s ease"
                              px={{ base: 4, md: 10 }} py={{ base: 8, md: 12 }}
                              textAlign="center" cursor="pointer"
                              {...getConsentRootProps()}
                            >
                                <input {...getConsentInputProps()} />
                                <Flex justify="center" mb={4} opacity={0.8} _groupHover={{ opacity: 1 }}>
                                    <GoVideo size={40} />
                                </Flex>
                                <Heading size="sm" mb={1}>Drop video here to upload</Heading>
                                <Text fontSize="12px" color={mutedText}>
                                    You can upload videos up to 200 MB in size. We support MP4, MOV, and WebM formats.
                                </Text>
                                <Button colorScheme={'brand'} variant="link" mt={4} onClick={openConsentPicker}>Browse local files</Button>
                            </Box>
                        ) : (
                            <Box width={{ base: "100%", md: "500px" }}  aspectRatio={16 / 9} mx="auto" borderWidth="1px" borderRadius="md" borderColor="gray.200" overflow="hidden" position="relative" _hover={{ borderColor: "purple.400", border: "1px dashed" }}>
                            <IconButton
                                icon={<DeleteIcon />} size="sm" position="absolute" variant="ghost"
                                bg="rgba(0,0,0,0.5)" color="white" top="8px" right="8px" zIndex="2"
                                aria-label="Delete video" 
                                onClick={() => {
                                    setFieldValue("consentVideo", null);
                                    setFieldValue("consentDuration", 0);
                                    formik.setFieldError("consentDuration", undefined); 
                                }}
                                _hover={{ bg: "purple.400" }}
                            />
                            <video
                                controls
                                src={URL.createObjectURL(values.consentVideo)}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                            </Box>
                        )}

                        <Flex alignItems={'center'} justifyContent={'center'}>
                        {/* Inline Step 3 errors (name/consent) */}
                        {(touched.name && errors.name) || (touched.consentVideo && errors.consentVideo) ? (
                            <Text color="red.400" fontSize="sm">
                                {errors.name || errors.consentVideo}
                            </Text>
                            ) : null}

                            {/* NEW: duration > 2 min message for CONSENT (Step 3 only) */}
                            {errors.consentDuration && values.consentDuration > MIN_SECONDS && (
                            <Text color="red.400" fontSize="sm" mt={2} whiteSpace="pre-line">
                                {errors.consentDuration}
                            </Text>
                        )}
                        </Flex>
                        </VStack>
                    </Box>
                )}
            </>
          )}
        </ModalBody>

        {/* -------- Footer -------- */}
        {!isCreatingAvatar && (
            <ModalFooter>
            <Flex w="100%" justify="space-between" align="center">
                <Button
                leftIcon={<ArrowBackIcon />} variant="ghost" size="sm"
                onClick={() => setStep((s) => Math.max(1, s - 1))} isDisabled={step === 1}
                >
                Back
                </Button>

                {step < 3 ? (
                <Tooltip hasArrow label={continueTooltip} isDisabled={!(step === 2 && blockStep2)} placement="top">
                    <span>
                    <Button
                        size="sm"
                        onClick={() => setStep((s) => Math.min(3, s + 1))}
                        isDisabled={(step === 2 && blockStep2)}
                    >
                        Continue
                    </Button>
                    </span>
                </Tooltip>
                ) : (
                <Tooltip hasArrow label={submitTooltip} isDisabled={!blockSubmit} placement="top">
                    <span>
                    <Button
                        size={'sm'}
                        isLoading={isSubmitting || isCreatingAvatar}
                        onClick={handleSubmit}
                        isDisabled={blockSubmit}
                    >
                        Submit
                    </Button>
                    </span>
                </Tooltip>
                )}
            </Flex>
            </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}

/* ---------------- Entry wrapper ---------------- */
export default function CustomAvatarContent({selectedAvatar, onSelectAvatar}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { refetch } = useGetAvatarsQuery(); 

  return (
    <>
      <CreateAvatarCard onOpen={onOpen} selectedAvatar={selectedAvatar} onSelectAvatar={onSelectAvatar}/>
      <CreateAvatarModal isOpen={isOpen} onClose={onClose} refetchAvatars={refetch}/>
    </>
  );
}
